import { useState, useRef, useEffect } from 'react';
import { IRecipient, IDonor, ICompatibilityResult, ILkdpiResults, IDonorPreselectNote, ISingleEpletMmRisk } from '../../models/index';
import { FileDocumentType } from '@nkr/enums/index';
import { Alert, Button, ControlLabel, FormControl, InputGroup, Label, Row } from 'react-bootstrap';
import { ActionItems } from './action-items';
import HlaMatches from './hla-matches';
import DonorRecipientInfo from './donor-recipient-info';
import MedicalDemographicInfo from './medical-demographic-info';
import DonorAnatomy from './donor-anatomy';
import AdditionalDonorInfo from './additional-donor-info';
import ReactHtmlParser from 'react-html-parser';
import styled from 'styled-components';
import ActionsButton from './actions-button';

const LabelWrapper = styled(Label)`
    font-size: 100%;
`;

interface IProps {
    donor: IDonor,
    recipient: IRecipient,
    compatibilityResult: ICompatibilityResult,
    lkdpiResults: ILkdpiResults,
    compatiblePairLkdpiResults: ILkdpiResults,
    singleEpletMmRisk: ISingleEpletMmRisk,
    donorNote: IDonorPreselectNote,
    donorNotesContent: string,
    matchNote: IDonorPreselectNote,
    matchNotesContent: string,
    includeButtons: boolean,
    doesStudyExist: number,
    hasOutstandingOffer: boolean,
    isRecipientCenter: boolean,
    showNotes: boolean,
    isNkrLogin: boolean,
    swapNumber: string,
    matchHistoryId: number,
    onDonorNotesChange?: (contents: string) => void,
    onMatchNodesChange?: (contents: string) => void,
    onSaveNotes?: (isMatch: boolean) => void,
    onAcceptDonor?: (isFullyConfident: boolean) => void,
    onDeclineDonor?: (isGlobalDecline: boolean) => void,
    onRequestInfo?: () => void,
    onRequestExploratoryXm?: (freshBloodOverride: boolean) => void,
    onGetFile: (docType: FileDocumentType, donorId: number) => void,
    onViewCtScan: (donorId: number) => void
}

const Match = (props: IProps) => {
    const [actionIsOpen, setActionIsOpen] = useState(false);
    const screeningNoteRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (screeningNoteRef.current) {
            screeningNoteRef.current.style.height = 'auto';
            screeningNoteRef.current.style.height = `${screeningNoteRef.current.scrollHeight}px`;
        }
    }, [props.recipient.screeningComments]);

    const getPrevious = () => {
        if (props.compatibilityResult.isHardBlocked) {
            return <><LabelWrapper bsStyle='success'>rejected</LabelWrapper> this donor</>;
        }

        return <>accepted this donor</>;
    };

    const handleActionToggle = () => {
        setActionIsOpen(!actionIsOpen);
    };

    const handleActionItem = (eventKey: any) => {
        setActionIsOpen(false);

        let { donor } = props;

        switch (eventKey) {
            case ActionItems.AcceptableFullyConfident:
                props.onAcceptDonor(true);
                break;

            case ActionItems.AcceptableNotFullyConfident:
                props.onAcceptDonor(false);
                break;

            case ActionItems.NotAcceptable:
                props.onDeclineDonor(false);
                break;

            case ActionItems.DeclineForAllRecipients:
                props.onDeclineDonor(true);
                break;

            case ActionItems.DonorHlaFile:
                props.onGetFile(FileDocumentType.DonorHistoTyping, donor.id);
                break;

            case ActionItems.DonorMolecularAboFile:
                props.onGetFile(FileDocumentType.DonorHistoAbo, donor.id);
                break;

            case ActionItems.Donor3GTypingReport:
                props.onGetFile(FileDocumentType.DonorHistoTyping, donor.id);
                break;

            case ActionItems.VRLConfirmatoryTypingReport:
                props.onGetFile(FileDocumentType.DonorVRLHLA, donor.id);
                break;

            case ActionItems.ViewDonorCTStudy:
                handleCtView(donor.id)
                break;

            case ActionItems.DonorCTReport:
                props.onGetFile(FileDocumentType.DonorCTReport, donor.id);
                break;

            case ActionItems.RequestInfo:
                props.onRequestInfo();
                break;

            case ActionItems.RequestExm:
                props.onRequestExploratoryXm(false);
                break;

            case ActionItems.RequestExmFreshBlood:
                props.onRequestExploratoryXm(true);
                break;
        }
    };

    const handleCtView = (donorId: number) => {
        props.onViewCtScan(donorId);
    };

    const { donor, recipient, lkdpiResults, compatiblePairLkdpiResults, singleEpletMmRisk, includeButtons, doesStudyExist } = props;

    return (
        <div className='container'>
            <div className='row'>
                <div className='col-xs-12'>
                    {(props.compatibilityResult.donor.isAccepted || props.compatibilityResult.isHardBlocked || props.compatibilityResult.donor.isReEvalNeeded) && (
                        <Alert bsStyle='warning' bsSize='sm'>
                            {props.compatibilityResult.donor.isReEvalNeeded ?
                                <span>
                                    ATTENTION: You previously {
                                        props.compatibilityResult.donor.previousIsAccepted === true ? <strong>ACCEPTED</strong>
                                        : props.compatibilityResult.donor.previousIsAccepted === false ? <strong>DECLINED</strong>
                                        : 'reviewed'
                                    } this donor, but the donor's following medical data has been updated and a re-evaluation is necessary.
                                    {props.compatibilityResult.donor.updatedDonorItems != '' &&
                                        <><br />{props.compatibilityResult.donor.updatedDonorItems
                                            .replace(/<BR>/gi, ' ')
                                            .replace(/<ul>/gi, ' ')
                                            .replace(/<\/ul>/gi, '')
                                            .replace(/<li>/gi, '')
                                            .replace(/<\/li>/gi, ', ')
                                            .replace(/,\s*$/, '')
                                            .replace(/\s+/g, ' ')
                                            .trim()
                                        }</>
                                    }
                                </span>
                                :
                                <span>ATTENTION: You previously {getPrevious()}.</span>
                            }
                        </Alert>
                    )}

                    {includeButtons && (
                        <>
                            <div className='row' style={{ marginBottom: 20 }}>
                                <div className='col-xs-12 col-sm-10 col-print-12'>
                                    <h2 style={{ margin: 0 }}>Evaluate Donor {props.compatibilityResult.donor.alias} at {props.compatibilityResult.donor.rdnCenterId > 0 ? props.compatibilityResult.donor.rdnCenterAlias : props.compatibilityResult.donor.center.alias} {props.swapNumber != '' ? <span style={{ color: 'red' }}>(Swap {props.swapNumber})</span> : null}</h2>
                                </div>
                                <div className='col-xs-12 col-sm-2 no-print'>
                                    <ActionsButton
                                        donor={props.compatibilityResult.donor}
                                        recipient={props.recipient}
                                        doesStudyExist={doesStudyExist}
                                        hasOutstandingOffer={props.hasOutstandingOffer}
                                        isOpen={actionIsOpen}
                                        showMatchDecisionOptions={true}
                                        isNkrLogin={props.isNkrLogin}
                                        matchHistoryId={props.matchHistoryId}
                                        onActionToggle={handleActionToggle}
                                        onActionItem={handleActionItem}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {props.isRecipientCenter && props.showNotes && (
                        <>
                            <Row style={{ marginBottom: 10 }}>
                                <div className='col-xs-12'>
                                    <InputGroup>
                                        <InputGroup.Addon>Donor Note:</InputGroup.Addon>
                                        <FormControl
                                            placeholder='Donor notes you enter here will be visible for ALL recipients when evaluating this donor'
                                            value={props.donorNotesContent}
                                            onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => props.onDonorNotesChange(e.currentTarget.value)}
                                        />
                                        <InputGroup.Button>
                                            <Button bsStyle='primary' onClick={() => props.onSaveNotes(false)}>Save</Button>
                                        </InputGroup.Button>
                                    </InputGroup>
                                </div>
                            </Row>

                            <Row style={{ marginBottom: 10 }}>
                                <div className='col-xs-12'>
                                    <InputGroup>
                                        <InputGroup.Addon>Match Note:</InputGroup.Addon>
                                        <FormControl
                                            placeholder='Match notes you enter here will only be visible for this recipient when evaluating this donor'
                                            value={props.matchNotesContent}
                                            onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => props.onMatchNodesChange(e.currentTarget.value)}
                                        />
                                        <InputGroup.Button>
                                            <Button bsStyle='primary' onClick={() => props.onSaveNotes(true)}>Save</Button>
                                        </InputGroup.Button>
                                    </InputGroup>
                                </div>
                            </Row>
                        </>
                    )}

                    {props.isRecipientCenter && props.showNotes == false && (
                        <>
                            <Row style={{ marginBottom: 10 }}>
                                <div className='col-xs-12'>
                                    <ControlLabel>Donor Note</ControlLabel>
                                    <div>{props.donorNotesContent ? props.donorNotesContent : '[not specified]'}</div>
                                </div>
                            </Row>

                            <Row style={{ marginBottom: 10 }}>
                                <div className='col-xs-12'>
                                    <ControlLabel>Match Note</ControlLabel>
                                    <div>{props.matchNotesContent ? props.matchNotesContent : '[not specified]'}</div>
                                </div>
                            </Row>
                        </>
                    )}

                    <div style={{ marginBottom: 20 }}>
                        <InputGroup>
                            <InputGroup.Addon>Recipient Screening Note:</InputGroup.Addon>
                            <FormControl
                                componentClass='textarea'
                                readOnly={true}
                                value={recipient.screeningComments}
                                inputRef={(ref: HTMLInputElement) => { screeningNoteRef.current = ref; }}
                                style={{ resize: 'none', overflow: 'hidden' }}
                            />
                        </InputGroup>
                    </div>

                    <Row>
                        <div className='col-xs-12 col-sm-4'>
                            <DonorRecipientInfo
                                donor={donor}
                                recipient={recipient}
                                lkdpiResults={lkdpiResults}
                                compatiblePairLkdpiResults={compatiblePairLkdpiResults}
                                singleEpletMmRisk={singleEpletMmRisk}
                            />
                        </div>
                        <div className='col-xs-12 col-sm-4'>
                            <DonorAnatomy
                                donor={donor}
                            />
                        </div>
                        <div className='col-xs-12 col-sm-4'>
                            <MedicalDemographicInfo
                                donor={donor}
                                recipient={recipient}
                            />
                        </div>
                    </Row>

                    <AdditionalDonorInfo
                        donor={donor}
                        recipient={recipient}
                        compatibilityResult={props.compatibilityResult}
                        includeButtons={props.includeButtons}
                        doesStudyExist={props.doesStudyExist}
                        onActionItem={handleActionItem}
                        onViewCtScan={props.onViewCtScan}
                    />

                    <div className='row'>
                        <div className='col-xs-12'>
                            <HlaMatches
                                donor={props.compatibilityResult.donor}
                                recipient={props.recipient}
                                compatibilityResult={props.compatibilityResult}
                                showAvoidsList={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Match;